import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { EnfafDatetimePickerDarkComponent } from '../../../../components/enfaf-datetime-picker-dark/enfaf-datetime-picker-dark.component';
import { EnfafExpansionPanelLightComponent } from '../../../../components/enfaf-expansion-panel-light/enfaf-expansion-panel-light.component';
import { EnfafImgDraggerComponent } from '../../../../components/enfaf-img-dragger/enfaf-img-dragger.component';
import { EnfafInputComponent } from '../../../../components/enfaf-input/enfaf-input.component';
import { EnfafModalComponent } from '../../../../components/enfaf-modal/enfaf-modal.component';
import { EnfafSelectLightComponent } from '../../../../components/enfaf-select-light/enfaf-select-light.component';
import { EventRecurrence } from '../../../../models/enums/event-recurrence.enum';
import { EventSegmentationType } from '../../../../models/enums/event-segmentation-type.enum';
import { Event } from '../../../../models/interfaces/event.interface';
import { ApiService } from '../../../../core/services/api/api.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { eventPlatformOptions, eventRecurrenceOptions, eventSegmentationTypeOptions } from './events-form.constants';
import { eventsFormErrors } from './events-form.errors';
import { endDateValidator, startDateValidator, urlValidator } from './events-form.validators';
import { EventManagementComponent } from '../event-management/event-management.component';
import { GoogleOauthComponent } from '../../../../components/google-oauth/google-oauth.component';
import { EventPlatform } from '../../../../models/enums/event-platform.enum';
import { convertDateToISOString } from '../../../../shared/utils/convert-date-to-iso-string.util';

@Component({
  selector: 'app-events-form',
  imports: [
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    EnfafModalComponent,
    EnfafExpansionPanelLightComponent,
    EnfafInputComponent,
    EnfafSelectLightComponent,
    EnfafImgDraggerComponent,
    EnfafDatetimePickerDarkComponent,
    EventManagementComponent,
    GoogleOauthComponent
  ],
  templateUrl: './events-form.component.html',
  styleUrl: './events-form.component.scss'
})
export class EventsFormComponent implements OnInit, OnDestroy {
  @ViewChild(EventManagementComponent) eventManagement?: EventManagementComponent;
  @ViewChild(GoogleOauthComponent) googleOauth?: GoogleOauthComponent;

  eventForm!: FormGroup;
  isLoadingEvent = false;
  isSavingEvent = false;
  eventData: Event | null = null;

  // Track when a remote image was deleted so save is allowed (similar to modal-edit-product)
  private isDeletingRemoteImage = false;

  // Image handling properties (following promotions pattern)
  imageToUpload: File | null = null;
  imageToUploadSrc: string | null = null;

  // Options for selects
  segmentationTypeOptions = eventSegmentationTypeOptions;
  platformOptions = eventPlatformOptions;
  recurrenceOptions = eventRecurrenceOptions;

  // Type-safe getters for form controls
  get startDateControl(): FormControl<Date | null> {
    return this.eventForm.get('startDate') as FormControl<Date | null>;
  }

  get endDateControl(): FormControl<Date | null> {
    return this.eventForm.get('endDate') as FormControl<Date | null>;
  }

  get segmentationTypeControl(): FormControl<EventSegmentationType | null> {
    return this.eventForm.get('segmentationType') as FormControl<EventSegmentationType | null>;
  }

  get confirmButtonDisabled(): boolean {
    const isPlatformGoogleMeetConnected = this.isPlatformGoogleMeet ? this.googleOauth?.isConnected : true;
    let areSomeFieldsChanged = false;
    if (this.eventData) {
      const isTitleChanged = this.eventForm.get('title')?.value !== this.eventData.title;
      const isSubtitleChanged = (this.eventForm.get('subtitle')?.value || null) !== this.eventData.subtitle;
      const isSegmentationTypeChanged = this.eventForm.get('segmentationType')?.value !== this.eventData.segmentationType;

      // Handle Luxon DateTime conversion
      const startDateValue = this.eventForm.get('startDate')?.value;
      const startDateISO = startDateValue ? convertDateToISOString(startDateValue) : null;
      const originalStartDateISO = this.eventData.startDate ? new Date(this.eventData.startDate).toISOString() : null;
      const isStartDateChanged = startDateISO !== originalStartDateISO;

      // Handle Luxon DateTime conversion for end date
      const endDateValue = this.eventForm.get('endDate')?.value;
      const endDateISO = endDateValue ? convertDateToISOString(endDateValue) : null;
      const originalEndDateISO = this.eventData.endDate ? new Date(this.eventData.endDate).toISOString() : null;
      const isEndDateChanged = endDateISO !== originalEndDateISO;
      const isRecurrenceChanged = this.eventForm.get('advanced.recurrence')?.value !== this.eventData.advanced?.recurrence;
      const isPlatformChanged = this.eventForm.get('advanced.platform')?.value !== this.eventData.advanced?.platform;
      const isButtonTextChanged = this.eventForm.get('advanced.buttonText')?.value !== this.eventData.advanced?.buttonText;
      const isButtonLinkChanged = this.eventForm.get('advanced.buttonLink')?.value !== this.eventData.advanced?.buttonLink;
      // Image change detection: follow product modal approach — treat local upload or deletion as change
      const isEventImageChanged = !!this.imageToUpload || this.isDeletingRemoteImage;

      // Check if any of the fields have changed
      areSomeFieldsChanged =
        isTitleChanged ||
        isSubtitleChanged ||
        isSegmentationTypeChanged ||
        isStartDateChanged ||
        isEndDateChanged ||
        isRecurrenceChanged ||
        isPlatformChanged ||
        isButtonTextChanged ||
        isButtonLinkChanged ||
        isEventImageChanged;
    }
    return this.eventForm.invalid || this.isSavingEvent || !isPlatformGoogleMeetConnected || (!!this.eventData && !areSomeFieldsChanged);
  }

  // Error handling getters
  private errors = eventsFormErrors;
  private destroyed$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { eventId?: string },
    private dialogRef: MatDialogRef<EventsFormComponent>,
    private fb: FormBuilder,
    private api: ApiService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.initForm();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  buildForm(): void {
    // Calculate default start date (current time + 15 minutes)
    const defaultStartDate = new Date();
    defaultStartDate.setMinutes(defaultStartDate.getMinutes() + 15);
    defaultStartDate.setSeconds(0, 0);

    this.eventForm = this.fb.group({
      // Contenido section
      eventImageUrl: this.fb.control<string | null>(null, []),
      title: this.fb.control<string>('', [Validators.required, Validators.maxLength(255)]),
      subtitle: this.fb.control<string>('', [Validators.maxLength(255)]),

      // Gestión del evento section
      segmentationType: this.fb.control<string>(EventSegmentationType.AllStudents, [Validators.required]),
      startDate: this.fb.control<Date>(defaultStartDate, [Validators.required, startDateValidator]),
      endDate: this.fb.control<Date | null>(null, [endDateValidator]),

      status: this.fb.control<boolean>(true, []),

      // Avanzado section
      advanced: this.fb.group({
        recurrence: this.fb.control<string>(EventRecurrence.NotRepeating, []),
        platform: this.fb.control<string>(EventPlatform.Custom, []),
        buttonText: this.fb.control<string>('Ir a la clase', [Validators.required, Validators.maxLength(50)]),
        buttonLink: this.fb.control<string>('', [urlValidator])
      })
    });

    // Subscribe to platform changes to update endDate and buttonLink validation
    this.eventForm
      .get('advanced.platform')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((platform) => {
        this.updateEndDateValidation(platform);
        this.updateButtonLinkValidation(platform);
      });

    if (this.eventData) {
      this.populateForm(this.eventData);
    } else {
      this.updateEndDateValidation(EventPlatform.Custom);
      this.updateButtonLinkValidation(EventPlatform.Custom);
    }
  }

  private updateEndDateValidation(platform: string): void {
    const endDateControl = this.eventForm.get('endDate');
    if (!endDateControl) return;

    if (platform === EventPlatform.Discord) {
      // Discord requires end date
      endDateControl.setValidators([Validators.required, endDateValidator]);
    } else {
      // Other platforms don't require end date
      endDateControl.setValidators([endDateValidator]);
    }

    endDateControl.updateValueAndValidity();
  }

  private updateButtonLinkValidation(platform: string): void {
    const buttonLinkControl = this.eventForm.get('advanced.buttonLink');
    if (!buttonLinkControl) return;

    if (platform === EventPlatform.Custom) {
      // Custom platform requires button link
      buttonLinkControl.setValidators([Validators.required, urlValidator]);
    } else {
      // Other platforms don't require button link (optional)
      buttonLinkControl.setValidators([urlValidator]);
    }

    buttonLinkControl.updateValueAndValidity();
  }

  private resteForm(): void {
    // Calculate default start date (current time + 15 minutes)
    const defaultStartDate = new Date();
    defaultStartDate.setMinutes(defaultStartDate.getMinutes() + 15);
    defaultStartDate.setSeconds(0, 0);

    this.eventForm.reset({
      eventImageUrl: null,
      title: '',
      subtitle: '',
      segmentationType: EventSegmentationType.AllStudents,
      startDate: defaultStartDate,
      endDate: null,
      status: true,
      advanced: {
        recurrence: EventRecurrence.NotRepeating,
        platform: EventPlatform.Custom,
        buttonText: 'Ir a la clase',
        buttonLink: ''
      }
    });
  }

  async initForm(): Promise<void> {
    this.isLoadingEvent = true;
    try {
      const eventId = (this.data && this.data.eventId) || null;

      if (eventId) {
        const response = await firstValueFrom(this.api.fetchEventById(eventId));
        if (response && response.event) {
          this.eventData = response.event;
          this.populateForm(this.eventData);
        }
      }
    } catch (error) {
      this.eventData = null;
      this.toast.presentErrorToast('Hubo un error obteniendo el evento');
    } finally {
      this.isLoadingEvent = false;
    }
  }

  populateForm(eventData: Event): void {
    if (!eventData) return;

    this.eventForm.patchValue({
      eventImageUrl: eventData.eventImageUrl || null,
      title: eventData.title,
      subtitle: eventData.subtitle || '',
      segmentationType: eventData.segmentationType,
      startDate: eventData.startDate ? new Date(eventData.startDate) : null,
      endDate: eventData.endDate ? new Date(eventData.endDate) : null,
      status: eventData.status,
      advanced: {
        recurrence: eventData.advanced?.recurrence || EventRecurrence.NotRepeating,
        platform: eventData.advanced?.platform || '',
        buttonText: eventData.advanced?.buttonText || '',
        buttonLink: eventData.advanced?.buttonLink || ''
      }
    });

    // In edit mode, do NOT apply startDateValidator (only require the field)
    const startDateControl = this.eventForm.get('startDate');
    if (startDateControl) {
      startDateControl.setValidators([Validators.required]);
      startDateControl.updateValueAndValidity();
    }

    // Update endDate validation based on platform
    this.updateEndDateValidation(eventData.advanced?.platform || '');

    // Update buttonLink validation based on platform
    this.updateButtonLinkValidation(eventData.advanced?.platform || '');

    // Populate course segmentations if needed
    if (
      eventData.segmentationType === EventSegmentationType.RegisteredStudentsOnly &&
      eventData.courseSegmentations &&
      eventData.courseSegmentations.length > 0
    ) {
      // Map course segmentations to Course objects
      const courses = eventData.courseSegmentations
        .filter((cs) => cs.course)
        .map((cs) => ({
          courseId: cs.course!.courseId,
          name: cs.course!.name,
          shortDescription: '',
          description: '',
          purchaseDate: '',
          classesCompleted: 0,
          totalClasses: 0,
          sections: [],
          discord: null,
          whatsapp: null,
          disabled: false,
          exams: [],
          type: 'PAID' as any,
          requiresDocuments: null,
          requiredDocuments: null,
          documents: null,
          finalEmailSubject: null,
          finalEmailMessage: null,
          finalEmailAttachments: [],
          generateEnrollmentOrder: false,
          accreditationCenter: null
        }));

      // Set the selected courses in the event management component
      setTimeout(() => {
        if (this.eventManagement) {
          this.eventManagement.setSelectedCourses(courses);
        }
      }, 100);
    }
  }

  // Error getters
  get titleErrors(): string {
    const control = this.eventForm.get('title');
    if (control?.touched && control?.errors) {
      if (control.errors['required']) return this.errors.title.required;
      if (control.errors['minlength']) return this.errors.title.minlength;
      if (control.errors['maxlength']) return this.errors.title.maxlength;
    }
    return '';
  }

  get subtitleErrors(): string {
    const control = this.eventForm.get('subtitle');
    if (control?.touched && control?.errors) {
      if (control.errors['maxlength']) return this.errors.subtitle.maxlength;
    }
    return '';
  }

  get segmentationTypeErrors(): string {
    const control = this.eventForm.get('segmentationType');
    if (control?.touched && control?.errors) {
      if (control.errors['required']) return this.errors.segmentationType.required;
    }
    return '';
  }

  get linkErrors(): string {
    const control = this.eventForm.get('link');
    if (control?.touched && control?.errors) {
      if (control.errors['required']) return this.errors.link.required;
      if (control.errors['invalidUrl']) return this.errors.link.invalidUrl;
    }
    return '';
  }

  get startDateErrors(): string {
    const control = this.eventForm.get('startDate');
    if (control?.touched && control?.errors) {
      if (control.errors['required']) return this.errors.startDate.required;
      if (control.errors['startDateTooSoon']) return 'La fecha de inicio debe ser al menos 15 minutos después de la hora actual';
      if (control.errors['startDateAfterEndDate']) return this.errors.startDate.startDateAfterEndDate;
    }
    return '';
  }

  get endDateErrors(): string {
    const control = this.eventForm.get('endDate');
    if (control?.touched && control?.errors) {
      if (control.errors['required']) return 'La fecha de fin es requerida para eventos de Discord';
      if (control.errors['endDateBeforeStartDate']) return this.errors.endDate.endDateBeforeStartDate;
      if (control.errors['endDateBeyondWeekEnd']) return this.errors.endDate.endDateBeyondWeekEnd;
    }
    return '';
  }

  get buttonTextErrors(): string {
    const control = this.eventForm.get('advanced.buttonText');
    if (control?.touched && control?.errors) {
      if (control.errors['required']) return this.errors['advanced.buttonText'].required;
      if (control.errors['minlength']) return this.errors['advanced.buttonText'].minlength;
      if (control.errors['maxlength']) return this.errors['advanced.buttonText'].maxlength;
    }
    return '';
  }

  get buttonLinkErrors(): string {
    const control = this.eventForm.get('advanced.buttonLink');
    if (control?.touched && control?.errors) {
      if (control.errors['required']) return 'El enlace del botón es requerido para plataformas personalizadas';
      if (control.errors['invalidUrl']) return this.errors['advanced.buttonLink'].invalidUrl;
    }
    return '';
  }

  get isPlatformGoogleMeet(): boolean {
    const control = this.eventForm.get('advanced.platform');
    return control?.value === EventPlatform.GoogleMeet;
  }

  get isPlatformDiscord(): boolean {
    const control = this.eventForm.get('advanced.platform');
    return control?.value === EventPlatform.Discord;
  }

  get isPlatformCustom(): boolean {
    const control = this.eventForm.get('advanced.platform');
    return control?.value === EventPlatform.Custom;
  }

  // Image handling methods (following promotions pattern)
  uploadImage(data: { imageToUpload: File | null; imageToUploadSrc: string | null }): void {
    this.imageToUpload = data.imageToUpload;
    this.imageToUploadSrc = data.imageToUploadSrc;
  }

  deleteImage(): void {
    const hadRemoteImage = !!this.eventForm.get('eventImageUrl')?.value;

    this.imageToUpload = null;
    this.imageToUploadSrc = null;
    this.eventForm.patchValue({
      eventImageUrl: null
    });
    this.eventForm.get('eventImageUrl')?.updateValueAndValidity();

    // If we deleted an existing remote image, mark flag so save is allowed
    if (hadRemoteImage && this.eventData && this.eventData.eventImageUrl) {
      this.isDeletingRemoteImage = true;
      this.eventForm.markAsDirty();
    }
  }

  handleCancel() {
    this.resteForm();
    this.dialogRef.close({
      result: false,
      data: null
    });
  }

  async handleSave() {
    if (this.eventForm.invalid) {
      this.eventForm.markAllAsTouched();
      return;
    }

    try {
      this.isSavingEvent = true;

      const formValue = this.eventForm.value;

      // Get selected courses from event management component
      const selectedCourses = this.eventManagement?.getSelectedCourses() || [];

      // Create FormData for file upload
      const eventFormData = new FormData();

      // Prepare event data
      const eventData = {
        title: formValue.title!,
        subtitle: formValue.subtitle || null,
        eventImageUrl: formValue.eventImageUrl || null,
        segmentationType: formValue.segmentationType!,
        link: formValue.advanced?.buttonLink || '',
        startDate: convertDateToISOString(formValue.startDate!),
        endDate: formValue.endDate ? convertDateToISOString(formValue.endDate) : null,
        status: formValue.status,
        advanced: {
          recurrence: formValue.advanced?.recurrence || 'none',
          platform: formValue.advanced?.platform || '',
          buttonText: formValue.advanced?.buttonText || '',
          buttonLink: formValue.advanced?.buttonLink || ''
        },
        // Include course segmentations if applicable
        courseSegmentations:
          formValue.segmentationType === EventSegmentationType.RegisteredStudentsOnly
            ? selectedCourses.map((course) => ({ courseId: course.courseId }))
            : []
      };

      // Add image to FormData if present
      if (this.imageToUpload) {
        eventFormData.append('eventImage', this.imageToUpload, this.imageToUpload.name);
      }

      let response;
      if (this.eventData) {
        // Update existing event
        eventFormData.append(
          'form',
          JSON.stringify({
            ...eventData,
            eventId: this.eventData.eventId
          })
        );
        response = await firstValueFrom(this.api.updateEvent(eventFormData));
        this.toast.presentSuccessToast(`El evento ${eventData.title} se ha actualizado correctamente`);
      } else {
        // Create new event
        eventFormData.append('form', JSON.stringify(eventData));
        response = await firstValueFrom(this.api.createEvent(eventFormData));
        this.toast.presentSuccessToast(`El evento ${eventData.title} se ha creado correctamente`);
      }

      // reset image deletion flag after successful save
      this.isDeletingRemoteImage = false;

      this.isSavingEvent = false;
      this.resteForm();

      this.dialogRef.close({
        result: true,
        data: response?.event || null
      });
    } catch (error: any) {
      this.isSavingEvent = false;
      let errorMessage = 'Hubo un error guardando el evento';
      if (error && error.error && error.error.message) {
        errorMessage = error.error.message;
      }
      this.toast.presentErrorToast(errorMessage);
      return;
    }
  }
}
